import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUniversity, FaShieldAlt, FaChartLine, FaGavel, FaCogs, FaUsers, FaLock, FaEye, FaHandshake, FaLightbulb, FaFileContract, FaCalculator } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(100px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FAQContainer = styled.div`
  display: flex;
  gap: 40px;
  width: 100%;
  background: white;
  padding: 40px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 10px;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 20px;
  }
`;

const FAQContent = styled.div`
  flex: 1;
  animation: ${slideInRight} 1s ease-in-out;
`;

const FAQItem = styled.div`
  background: #ffffff;
  margin: 15px 0;
  border-bottom: 1px solid #d1d5db;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  transform: translateY(20px);
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};

  &:hover {
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const Question = styled.div`
  background: #f9fafb;
  padding: 15px;
  font-size: 18px;
  font-weight: bold;
  color: #333;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.3s ease;

  &:hover {
    background: #eef1f6;
  }
`;

const Answer = styled.div`
  padding: 15px;
  font-size: 16px;
  color: #555;
  background: #ffffff;
  border-top: 1px solid #d1d5db;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const Icon = styled.span`
  font-size: 22px;
  font-weight: bold;
  color: #dc3545;
  transition: transform 0.3s ease;
  ${({ isOpen }) => isOpen && "transform: rotate(180deg);"}
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const AdvantageCard = styled.div`
  padding: 25px 15px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const AdvantageIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
  color: ${props => props.color || "#0d6efd"};
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const BanqueFinance = () => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const toggleFAQ = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const faqData = [
        {
            question: "Quelles sont les spécificités comptables du secteur BFA ?",
            answer: "Le secteur BFA est soumis à des règles comptables spécifiques comme le Plan Comptable Bancaire, les normes IFRS 9 pour les instruments financiers, IFRS 17 pour les contrats d'assurance, et les référentiels spécifiques aux SFD. Nous maîtrisons ces particularités pour assurer la conformité de vos états financiers et optimiser votre reporting."
        },
        {
            question: "Comment gérez-vous le reporting prudentiel ?",
            answer: "Nous élaborons et contrôlons votre reporting prudentiel destiné aux autorités de tutelle (BCEAO, CIMA, etc.). Notre équipe suit les évolutions réglementaires comme Bâle III/IV et Solvabilité II pour garantir votre conformité tout en optimisant vos ratios."
        },
        {
            question: "Proposez-vous un accompagnement pour la transformation digitale ?",
            answer: "Oui, nous accompagnons les institutions financières dans leurs projets de transformation digitale : évaluation de la rentabilité des investissements technologiques, gestion du changement, mise en place de nouveaux outils de pilotage, et adaptation aux nouvelles réglementations liées au digital."
        },
        {
            question: "Comment assurez-vous la conformité LCB-FT ?",
            answer: "Nous proposons un audit complet de vos dispositifs de Lutte contre le Blanchiment de Capitaux et le Financement du Terrorisme, ainsi qu'une assistance à la mise en conformité. Notre approche couvre la cartographie des risques, les procédures de vigilance et le reporting réglementaire."
        },
        {
            question: "Quels sont vos services d'audit spécialisés pour le secteur BFA ?",
            answer: "Nous réalisons des audits légaux (commissariat aux comptes), des audits contractuels sur des points spécifiques (ratios prudentiels, performance de portefeuilles), l'évaluation du contrôle interne, l'audit des systèmes d'information financiers et des missions d'attestation réglementaire."
        },
        {
            question: "Comment gérez-vous la fiscalité spécifique du secteur financier ?",
            answer: "La fiscalité BFA est complexe et évolutive. Nous gérons la fiscalité courante (IS, TVA sur commissions), optimisons la fiscalité des produits financiers, accompagnons lors des contrôles fiscaux et assurons une veille proactive sur les évolutions réglementaires locales et internationales."
        }
    ];

    const servicesData = [
        {
            icon: <FaFileContract />,
            title: "Expertise Comptable & Reporting",
            description: "Production des états financiers selon les normes spécifiques, accompagnement IFRS 9/17, reporting prudentiel et consolidation pour les groupes financiers.",
            color: "#0d6efd"
        },
        {
            icon: <FaGavel />,
            title: "Audit Légal & Contrôle Interne",
            description: "Commissariat aux comptes, audit contractuel, évaluation du contrôle interne et audit des systèmes d'information financiers.",
            color: "#198754"
        },
        {
            icon: <FaCalculator />,
            title: "Conseil Fiscal Spécialisé",
            description: "Optimisation fiscale des produits financiers, gestion de la fiscalité courante et accompagnement lors des contrôles fiscaux.",
            color: "#dc3545"
        },
        {
            icon: <FaLock />,
            title: "Conformité LCB-FT",
            description: "Audit et assistance à la mise en conformité de votre dispositif de lutte contre le blanchiment et le financement du terrorisme.",
            color: "#6f42c1"
        },
        {
            icon: <FaChartLine />,
            title: "Conseil en Performance",
            description: "Tableaux de bord de pilotage, business plans, évaluation d'entreprise et accompagnement dans les projets de transformation.",
            color: "#fd7e14"
        },
        {
            icon: <FaShieldAlt />,
            title: "Gestion des Risques",
            description: "Conseil en gestion des risques opérationnels, de crédit et de marché, mise en place de dispositifs de gouvernance robustes.",
            color: "#20c997"
        }
    ];

    const advantagesData = [
        {
            icon: <FaUsers />,
            title: "Équipe Dédiée",
            color: "#0d6efd"
        },
        {
            icon: <FaEye />,
            title: "Approche Proactive",
            color: "#198754"
        },
        {
            icon: <FaLock />,
            title: "Confidentialité Absolue",
            color: "#dc3545"
        },
        {
            icon: <FaHandshake />,
            title: "Partenariat Long Terme",
            color: "#ffc107"
        }
    ];

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-8">
                            <h1 className="fw-bold display-4 mb-3">Banque, Finance & Assurance</h1>
                            <p className="fs-5 mb-4">Anticipez les évolutions pour transformer vos obligations en opportunités de croissance</p>
                            <nav aria-label="breadcrumb">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/" className="text-white text-decoration-none">
                                            Accueil
                                        </Link>{" "}
                                        /{" "}
                                        <Link to="/services" className="text-white text-decoration-none">
                                            Services
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item active text-white" aria-current="page">
                                        Banque, Finance & Assurance
                                    </li>
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                Notre Expertise au Service du Secteur Banque, Finance et Assurance
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                               Le secteur de la Banque, de la Finance et de l'Assurance fait face à des défis sans précédent : une pression réglementaire accrue 
                               (Bâle III/IV, Solvabilité II, IFRS 9/17), une transformation digitale accélérée, et la nécessité de gérer des risques 
                               de plus en plus complexes. Dans ce contexte exigeant, la simple conformité ne suffit plus. 
                               Vous avez besoin d'un partenaire qui non seulement comprend les subtilités de votre secteur, mais 
                               qui anticipe également les évolutions pour transformer vos obligations en opportunités de croissance.
                            </p>
                        </div>
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <div className="bg-light p-5 rounded-3">
                                <h3 className="fw-bold custom-title mb-4">
                                    Pourquoi choisir CINEF-EXPERTISE COMPTABLE pour le secteur BFA ?
                                </h3>
                                <div className="row">
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaUsers className="fs-1 text-primary" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Équipe dédiée et formée</h5>
                                                <p className="text-muted">
                                                    Nos collaborateurs sont formés en continu sur les spécificités du secteur BFA pour vous apporter des conseils pertinents et à forte valeur ajoutée.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaEye className="fs-1 text-success" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Approche proactive</h5>
                                                <p className="text-muted">
                                                    Nous vous informons des évolutions réglementaires et vous proposons des solutions en amont, sans attendre les échéances.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaLock className="fs-1 text-warning" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Confidentialité absolue</h5>
                                                <p className="text-muted">
                                                    Nous garantissons une discrétion totale sur les informations et les missions que vous nous confiez, pilier de la confiance.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="d-flex">
                                            <div className="me-3">
                                                <FaHandshake className="fs-1 text-danger" />
                                            </div>
                                            <div>
                                                <h5 className="fw-bold">Partenariat long terme</h5>
                                                <p className="text-muted">
                                                    Nous nous positionnons comme un véritable partenaire de votre croissance, engagé à vos côtés pour relever vos défis.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h2 className="fw-bold mb-1">Nos Services Dédiés au Secteur BFA</h2>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                            <p className="text-muted fs-5 mb-5">CINEF-EC a développé une expertise pointue et une offre de services 
                                sur-mesure pour accompagner les institutions financières, les compagnies d'assurance, les sociétés
                                 de gestion d'actifs et les établissements de microfinance. Notre mission : sécuriser votre activité, 
                                 optimiser votre performance et vous apporter la clarté nécessaire pour piloter votre stratégie avec confiance.</p>
                        </div>
                    </div>

                    <div className="row mb-4">
                        {servicesData.slice(0, 3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index}>
                                <ServiceCard delay={`${index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        {servicesData.slice(3).map((service, index) => (
                            <div className="col-md-6 col-lg-4 mb-4" key={index + 3}>
                                <ServiceCard delay={`${(index + 3) * 0.1}s`}>
                                    <ServiceIcon color={service.color}>{service.icon}</ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Sécurisez et Valorisez votre Développement</h3>
                                <p className="text-white fs-5 mb-4">
                                    Vos enjeux méritent l'attention d'experts qui parlent votre langage. 
                                    Discutons de vos besoins spécifiques et découvrez comment nous pouvons vous accompagner.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Planifier un Entretien</a>
                            </div>
                        </div>
                    </div>

                    <div className="row mt-5">
                        <div className="col-12 text-center mb-5">
                            <h3 className="fw-bold">Nos Atouts pour le Secteur BFA</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        {advantagesData.map((item, index) => (
                            <div className="col-6 col-md-3" key={index}>
                                <AdvantageCard>
                                    <AdvantageIcon color={item.color}>{item.icon}</AdvantageIcon>
                                    <p className="fw-bold fs-5 mb-0">{item.title}</p>
                                </AdvantageCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa" id="faq-section" className={isVisible ? 'visible' : ''}>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12 text-center">
                            <h3 className="fw-bold">Questions Fréquentes sur nos Services BFA</h3>
                            <div className="d-inline-block mb-4">
                                <div className="custom-underline mx-auto"></div>
                            </div>
                        </div>
                    </div>

                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            <FAQContainer>
                                <FAQContent>
                                    {faqData.map((faq, index) => (
                                        <FAQItem key={index} delay={`${index * 0.2}s`}>
                                            <Question onClick={() => toggleFAQ(index)}>
                                                {faq.question}
                                                <Icon isOpen={activeIndex === index}>{activeIndex === index ? "−" : "+"}</Icon>
                                            </Question>
                                            {activeIndex === index && <Answer>{faq.answer}</Answer>}
                                        </FAQItem>
                                    ))}
                                </FAQContent>
                            </FAQContainer>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, #008a44, #dc3545), 
                      url("/assets/images/image-banque-finance.jpg") no-repeat center center;
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
        }
      `}</style>
        </>
    );
};

export default BanqueFinance;